import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ArrowLeft, Check, Loader2, User, Mail, Phone, Briefcase,
  FileText, CreditCard, ChevronRight, Star, Music, Image, Calendar,
  Video, Shield, Zap, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

const PROFESSIONS = [
  { id: 'actor', label: 'Actor', icon: User },
  { id: 'musician', label: 'Musician / Band', icon: Music },
  { id: 'dancer', label: 'Dancer', icon: Star },
  { id: 'dj', label: 'DJ / Producer', icon: Zap },
  { id: 'agency', label: 'Agency', icon: Briefcase },
  { id: 'theatre', label: 'Theatre / Production', icon: Calendar },
  { id: 'photographer', label: 'Photographer', icon: Image },
  { id: 'other', label: 'Other', icon: Globe },
];

const STEPS = [
  { id: 'welcome', title: 'What do you do?' },
  { id: 'details', title: 'Your details' },
  { id: 'about', title: 'Tell us about you' },
  { id: 'package', title: 'Choose your package' },
  { id: 'payment', title: 'Payment' },
];

// Step indicator dots
function StepIndicator({ currentStep, totalSteps }) {
  return (
    <div className="flex items-center gap-2 justify-center mb-8" data-testid="step-indicator">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded-full transition-all duration-500",
            i === currentStep ? "w-8 bg-orange-500" : i < currentStep ? "w-4 bg-orange-300" : "w-4 bg-slate-700"
          )}
        />
      ))}
    </div>
  );
}

// Step 1: Profession
function WelcomeStep({ value, onChange }) {
  return (
    <div data-testid="step-welcome">
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">
        Let's build your <span className="text-orange-500">website</span>
      </h1>
      <p className="text-slate-400 mb-10 text-lg">First up — what best describes you?</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {PROFESSIONS.map(p => {
          const Icon = p.icon;
          const selected = value === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onChange(p.id)}
              data-testid={`profession-${p.id}`}
              className={cn(
                "flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-300",
                selected
                  ? "border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/10 scale-[1.02]"
                  : "border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800"
              )}
            >
              <Icon className={cn("w-7 h-7", selected ? "text-orange-500" : "text-slate-400")} />
              <span className={cn("text-sm font-medium", selected ? "text-orange-400" : "text-slate-300")}>{p.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Step 2: Details
function DetailsStep({ data, onChange }) {
  const update = (field, val) => onChange({ ...data, [field]: val });
  return (
    <div data-testid="step-details">
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">Your details</h1>
      <p className="text-slate-400 mb-10 text-lg">We just need the basics to get started</p>
      <div className="space-y-5 max-w-md">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1.5">First name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text" value={data.first_name} onChange={e => update('first_name', e.target.value)}
                placeholder="First name" data-testid="input-first-name"
                className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1.5">Last name</label>
            <input
              type="text" value={data.last_name} onChange={e => update('last_name', e.target.value)}
              placeholder="Last name" data-testid="input-last-name"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-400 mb-1.5">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="email" value={data.email} onChange={e => update('email', e.target.value)}
              placeholder="your@email.com" data-testid="input-email"
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-400 mb-1.5">Phone <span className="text-slate-600">(optional)</span></label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="tel" value={data.phone} onChange={e => update('phone', e.target.value)}
              placeholder="+44 7700 900000" data-testid="input-phone"
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 3: About
function AboutStep({ value, onChange }) {
  return (
    <div data-testid="step-about">
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">Tell us a bit about you</h1>
      <p className="text-slate-400 mb-10 text-lg">This helps us build the right site for you</p>
      <div className="max-w-md">
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="E.g. I'm a West End actor currently starring in... / We're a 5-piece band based in Manchester..."
          rows={5}
          data-testid="input-description"
          className="w-full px-5 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 resize-none text-base leading-relaxed"
        />
        <p className="text-xs text-slate-600 mt-2">This is optional but really helps us get your site right first time</p>
      </div>
    </div>
  );
}

// Step 4: Package Selection
function PackageStep({ packages, selected, onSelect }) {
  const pkgOrder = ['starter', 'professional', 'business'];
  const highlights = { starter: false, professional: true, business: false };

  return (
    <div data-testid="step-package">
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">Choose your package</h1>
      <p className="text-slate-400 mb-10 text-lg">Pick the plan that fits your career</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
        {pkgOrder.map(key => {
          const pkg = packages[key];
          if (!pkg) return null;
          const isSelected = selected === key;
          const isPro = highlights[key];
          const isFree = pkg.setup_fee === 0;
          return (
            <button
              key={key}
              onClick={() => onSelect(key)}
              data-testid={`package-${key}`}
              className={cn(
                "relative flex flex-col p-6 rounded-2xl border-2 text-left transition-all duration-300",
                isSelected
                  ? "border-orange-500 bg-orange-500/5 shadow-xl shadow-orange-500/10 scale-[1.02]"
                  : isPro
                    ? "border-orange-500/30 bg-slate-800/80 hover:border-orange-400/50"
                    : "border-slate-700 bg-slate-800/50 hover:border-slate-600"
              )}
            >
              {isPro && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="text-xl font-bold text-white mb-0.5">{pkg.name}</h3>
              <p className="text-xs text-slate-500 mb-4">{pkg.tagline}</p>
              <div className="mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-orange-500">£{pkg.monthly_price}</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>
                {isFree ? (
                  <p className="text-xs text-emerald-400 font-semibold mt-1">Free setup</p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">+ £{pkg.setup_fee} one-off setup</p>
                )}
              </div>
              <ul className="space-y-2.5 flex-1">
                {pkg.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>
              <div className={cn(
                "mt-5 py-2.5 rounded-xl text-center text-sm font-semibold transition-all",
                isSelected ? "bg-orange-500 text-white" : "bg-slate-700 text-slate-300"
              )}>
                {isSelected ? 'Selected' : 'Select'}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Step 5: Payment summary + consent
function PaymentStep({ data, packages }) {
  const pkg = packages[data.package];
  const isFree = pkg?.setup_fee === 0;
  return (
    <div data-testid="step-payment">
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 tracking-tight">Almost there</h1>
      <p className="text-slate-400 mb-10 text-lg">
        {isFree ? 'Review your details and get started' : 'Review your order and proceed to payment'}
      </p>
      <div className="max-w-md space-y-5">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Name</span>
            <span className="text-white font-medium">{data.first_name} {data.last_name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Email</span>
            <span className="text-white font-medium">{data.email}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Package</span>
            <span className="text-orange-400 font-bold">{pkg?.name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-400">Monthly</span>
            <span className="text-white font-medium">£{pkg?.monthly_price}/mo</span>
          </div>
          <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
            <span className="text-white font-semibold text-lg">Setup Fee</span>
            {isFree ? (
              <span className="text-2xl font-bold text-emerald-400">Free</span>
            ) : (
              <span className="text-2xl font-bold text-orange-500">£{pkg?.setup_fee}</span>
            )}
          </div>
        </div>
        {!isFree && (
          <p className="text-xs text-slate-500">
            Monthly billing of £{pkg?.monthly_price}/mo will begin after your site goes live.
          </p>
        )}
        <div className="flex items-start gap-2 text-xs text-slate-500">
          <Shield className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
          <span>
            {isFree 
              ? 'Your data is protected under GDPR. By proceeding you agree to our terms of service.'
              : 'Secure payment via Stripe. Your data is protected under GDPR. By proceeding you agree to our terms of service.'
            }
          </span>
        </div>
      </div>
    </div>
  );
}

// Success page
function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isFree = searchParams.get('free') === 'true';
  const [status, setStatus] = useState(isFree ? 'paid' : 'checking');
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  const pollStatus = useCallback(async () => {
    if (!sessionId || attempts >= 8) {
      setStatus(attempts >= 8 ? 'timeout' : 'error');
      return;
    }
    try {
      const res = await api.get(`/orders/payment-status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('paid');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    } catch {
      setAttempts(prev => prev + 1);
      setTimeout(pollStatus, 2000);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId && !isFree) pollStatus();
  }, [sessionId, isFree]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="order-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-white mb-2">Processing payment...</h1>
            <p className="text-slate-400">Just a moment while we confirm everything</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">You're all set!</h1>
            <p className="text-slate-400 mb-8">Payment confirmed. We've created your site and will start building it right away. You'll hear from us soon.</p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-all"
              data-testid="back-to-home-btn"
            >
              Back to Boxit
            </button>
          </>
        )}
        {(status === 'timeout' || status === 'error' || status === 'expired') && (
          <>
            <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
              <CreditCard className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {status === 'expired' ? 'Session expired' : 'Payment pending'}
            </h1>
            <p className="text-slate-400 mb-8">
              {status === 'expired'
                ? 'Your payment session has expired. Please try again.'
                : "We couldn't confirm your payment yet. If you've paid, don't worry — we'll process it shortly and be in touch."}
            </p>
            <button
              onClick={() => navigate('/order')}
              className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-all"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Main Onboarding Wizard
export default function OrderPage() {
  const [searchParams] = useSearchParams();
  const isSuccess = window.location.pathname.includes('/order/success');

  if (isSuccess) return <SuccessPage />;

  return <OnboardingWizard cancelled={searchParams.get('cancelled') === 'true'} />;
}

function OnboardingWizard({ cancelled }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [packages, setPackages] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    profession: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    description: '',
    package: searchParams.get('package') || 'professional',
  });

  useEffect(() => {
    api.get('/orders/packages').then(res => setPackages(res.data.packages)).catch(() => {});
    if (cancelled) toast.error('Payment was cancelled. You can try again.');
  }, [cancelled]);

  const canProceed = () => {
    switch (step) {
      case 0: return !!formData.profession;
      case 1: return formData.first_name && formData.last_name && formData.email && formData.email.includes('@');
      case 2: return true;
      case 3: return !!formData.package;
      case 4: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (!canProceed()) return;
    if (step === STEPS.length - 1) {
      handleSubmit();
      return;
    }
    setDirection(1);
    setStep(s => s + 1);
  };

  const goBack = () => {
    setDirection(-1);
    setStep(s => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const res = await api.post('/orders/create-checkout', {
        ...formData,
        origin_url: window.location.origin,
      });
      if (res.data.free_setup) {
        // Free setup — go straight to success
        navigate('/order/success?free=true');
      } else if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  const selectedPkg = packages[formData.package];
  const isFreeSetup = selectedPkg?.setup_fee === 0;

  const renderStep = () => {
    switch (step) {
      case 0: return <WelcomeStep value={formData.profession} onChange={v => setFormData(d => ({ ...d, profession: v }))} />;
      case 1: return <DetailsStep data={formData} onChange={d => setFormData(prev => ({ ...prev, ...d }))} />;
      case 2: return <AboutStep value={formData.description} onChange={v => setFormData(d => ({ ...d, description: v }))} />;
      case 3: return <PackageStep packages={packages} selected={formData.package} onSelect={v => setFormData(d => ({ ...d, package: v }))} />;
      case 4: return <PaymentStep data={formData} packages={packages} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      {/* Top bar */}
      <div className="px-6 py-5 flex items-center justify-between">
        <a href="/" className="text-2xl font-extrabold tracking-tight">
          <span className="text-orange-500">BOX</span><span className="text-white">IT</span>
        </a>
        <span className="text-xs text-slate-600 tracking-wider uppercase">Step {step + 1} of {STEPS.length}</span>
      </div>

      {/* Card area */}
      <div className="flex-1 flex items-center justify-center px-4 pb-32">
        <div className="w-full max-w-4xl">
          <StepIndicator currentStep={step} totalSteps={STEPS.length} />
          <div
            key={step}
            className="animate-in fade-in slide-in-from-right-5 duration-300"
            style={{ animationDirection: direction < 0 ? 'reverse' : 'normal' }}
          >
            {renderStep()}
          </div>
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={goBack}
            disabled={step === 0}
            data-testid="prev-step-btn"
            className={cn(
              "flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all",
              step === 0 ? "opacity-0 pointer-events-none" : "text-slate-400 hover:text-white hover:bg-slate-800"
            )}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <button
            onClick={goNext}
            disabled={!canProceed() || isSubmitting}
            data-testid="next-step-btn"
            className={cn(
              "flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all",
              canProceed() && !isSubmitting
                ? "bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20"
                : "bg-slate-800 text-slate-500 cursor-not-allowed"
            )}
          >
            {isSubmitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
            ) : step === STEPS.length - 1 ? (
              isFreeSetup
                ? <>Get Started <ArrowRight className="w-4 h-4" /></>
                : <><CreditCard className="w-4 h-4" /> Pay £{selectedPkg?.setup_fee || '...'}</>
            ) : (
              <>Continue <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
